import { DSBottomSheet } from '@repo/design-system/index'
import { QIcon_close_default, QIcon_volumne } from '@repo/icons/index'
import { useContext, useEffect, useState } from 'react'
import apis from '../../../modules/apis'
import { axiosPost } from '../../../modules/axiosHelper'
import { n_ai_tutor } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { PRONUNCIATION_API_HOST } from '../../../modules/vars'
import LoadingSpinner from '../../common/LoadingSpinner'
import { SoundContext, SoundProvider } from '../../common/audio/play/Audio'
import { ReactComponent as FeedbackEmpty } from '../../common/icons/feedback_empty.svg'
import { useMyJob } from './Hooks'

async function bufferToBase64(buffer) {
  const base64url = await new Promise<string>((r) => {
    const reader = new FileReader()
    reader.onload = () => r(reader.result as string)
    reader.readAsDataURL(new Blob([buffer]))
  })
  return base64url.slice(base64url.indexOf(',') + 1)
}

export const pronunciationInstant = async (audio: ArrayBuffer, transcription: string) => {
  const { token } = await axiosPost(apis.aiTutor.pronunciation.token())
  const url = `${PRONUNCIATION_API_HOST}/pronunciation_feedback`
  const res = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      audio_base64: await bufferToBase64(audio),
      transcription,
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  return res.json()
}

const WordButton = ({ word, active = false, onClick = null }) => (
  <div
    onClick={onClick}
    className={`rounded-lg border-solid border-[1.5px] pt-[8px] pr-[12px] pb-[8px] pl-[12px] flex flex-row gap-[4px] items-center justify-center shrink-0 relative ${
      active ? 'bg-purple-50 border-purple-500' : 'bg-white border-gray-300'
    }`}
  >
    <div className={`text-left text-[14px] leading-[20px] font-bold ${active ? 'text-primary-primary' : 'text-black'}`}>
      {word}
    </div>
  </div>
)

const PronunciationError = ({ info, key }) => {
  // TODO: update wording!
  const { t } = useTranslation()
  return (
    <div
      key={key}
      className="bg-white rounded-lg border-solid border-gray-200 border pt-[8px] pr-[16px] pb-[8px] pl-[16px] flex flex-col gap-[16px] items-end justify-start self-stretch shrink-0 relative"
    >
      <div className="flex flex-row items-center justify-between self-stretch shrink-0 relative">
        <div className="text-gray-700 text-left text-[16px] leading-[24px] font-medium relative">
          /{info.right}/을 /{info.wrong}/로 발음한 경우
        </div>
      </div>
    </div>
  )
}

const PronunciationFeedbackEmpty = () => {
  const { t } = useTranslation()

  return (
    <div className="bg-white pr-5 pl-5 flex flex-col gap-5 items-center justify-center self-stretch shrink-0 h-[225px] relative">
      <FeedbackEmpty width={64} height={64} />
      <div className="flex flex-col gap-3 items-center justify-start self-stretch shrink-0 relative">
        <div className="text-gray-500 text-center Title2-18Md self-stretch">
          {t(n_ai_tutor.ai_analysis_popup.feedback_none)}
        </div>
      </div>
    </div>
  )
}

const PronunciationFeedback = ({ data }) => {
  const { t } = useTranslation()
  const [index, setIndex] = useState(0)
  const errors = Object.entries(data?.word_error_dict || {}) as any
  const error = errors?.[index] || null
  const sound = useContext(SoundContext)
  const [showRecord, setShowRecord] = useState(false)
  const [customError, setCustomError] = useState({})

  const testSound = () => {
    setShowRecord(true)
  }

  const handleStopRecording = async (text, audio) => {
    setShowRecord(false)
    const res = await pronunciationInstant(audio, error[0])
    setCustomError((prev) => ({ ...prev, [error[0]]: res }))
  }

  if (!errors.length) return <PronunciationFeedbackEmpty />

  return (
    <div className="bg-white flex flex-col gap-[20px] items-center justify-start shrink-0 relative w-full">
      <div className="flex flex-col gap-[8px] items-start justify-start self-stretch shrink-0 relative">
        <div className="w-full overflow-x-auto">
          <div className="flex flex-row gap-[8px] items-start justify-start shrink-0 relative whitespace-nowrap pb-[8px]">
            {errors.map(([word, count], idx) => (
              <WordButton key={idx} word={word} active={idx === index} onClick={() => setIndex(idx)} />
            ))}
          </div>
        </div>
        <div className="text-gray-500 text-left text-[14px] leading-[20px] font-medium relative">
          {t(n_ai_tutor.study_activity.detail5)}
        </div>
      </div>
      {error && (
        <div className="flex flex-col gap-[8px] items-start justify-start self-stretch shrink-0 relative">
          <div className="bg-white rounded-xl border-solid border-gray-200 border p-[16px] flex flex-col gap-[16px] items-end justify-start self-stretch shrink-0 relative">
            <div className="bg-[#f8f9fb] rounded-lg p-[12px] flex flex-row gap-[10px] items-center justify-start self-stretch shrink-0 relative">
              <div
                className="shrink-0 w-[36px] h-[36px] relative rounded-[100px] flex items-center justify-center"
                style={{
                  boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.15)',
                }}
              >
                <QIcon_volumne className="w-[24px] h-[24px]" onClick={() => sound.play([error[1].audio_url])} />
              </div>
              <div className="text-center text-[20px] leading-[28px] font-bold relative">
                <span>
                  <span className="text-primary-primary">{error[0]}</span>
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-[8px] items-start justify-start self-stretch shrink-0 relative">
              {customError[error[0]] ? (
                <div className="flex flex-row items-center justify-between self-stretch shrink-0 relative">
                  <div className="text-gray-700 text-left text-[16px] leading-[24px] font-medium relative">
                    {Object.values(customError[error[0]].word_error_index || {})
                      .map((i: any) => i.errors)
                      .flat()
                      .map((info) => info.word_error_index)
                      .flat()
                      .map((info, index) => (
                        <PronunciationError key={index} info={info} />
                      ))}
                    {Object.keys(customError[error[0]].word_error_index || {}).length === 0 && <div>좋아요!</div>}
                  </div>
                </div>
              ) : (
                error[1]?.errors
                  ?.map((i) => i.word_error_index)
                  ?.flat()
                  ?.map((info, index) => <PronunciationError key={index} info={info} />)
              )}
            </div>
            {/* <div className="flex w-full justify-end">
              <div
                className="bg-white rounded-[100px] border-gray-200 border px-[16px] py-[12px] flex flex-row gap-[8px] items-center justify-start relative"
                onClick={testSound}
              >
                <div className="flex flex-row gap-[6px] items-start justify-start relative">
                  <QIcon_headphone />
                </div>
                <div className="text-gray-700 text-left Body2-14Bd relative">{t(n_ai_tutor.study_activity.btn4)}</div>
              </div>
            </div> */}
            {/* {showRecord && (
              <Record
                handleStopRecording={handleStopRecording}
                handleCancel={() => setShowRecord(false)}
                autoStart
                helper={t(n_ai_tutor.study_activity.detail2)}
              />
            )} */}
          </div>
        </div>
      )}
    </div>
  )
}

export const PronunciationFeedbackSheet = ({ show, setShow, audio, words }) => {
  const [data, setData] = useState(null)
  const runPronunciation = async (audio: ArrayBuffer) => {
    if (!audio) return
    setData(await pronunciationInstant(audio, words.map((word) => word.word).join(' ')))
  }

  const myJob = useMyJob()

  useEffect(() => {
    if (show && audio) runPronunciation(audio)
  }, [audio, show])

  return (
    show && (
      <SoundProvider>
        <DSBottomSheet
          enableScroll={true}
          closeOnDim={true}
          id="ai-tutor-pronunciation-feedback"
          isOpen={show}
          setIsOpen={setShow}
        >
          <div className="flex w-full justify-end px-[12px] pb-[12px]">
            <QIcon_close_default width={24} height={24} onClick={() => setShow(false)} />
          </div>
          <div className="flex flex-col items-start justify-start shrink-0 relative Body2-14Md p-[12px] gap-[12px]">
            {data ? (
              <>
                <PronunciationFeedback data={data} />
              </>
            ) : (
              <LoadingSpinner lg show primary />
            )}
          </div>
        </DSBottomSheet>
      </SoundProvider>
    )
  )
}
